import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module core/editor/editor
 */
import { Config, CKEditorError, ObservableMixin } from '@ckeditor/ckeditor5-utils';
import { Conversion, DataController, EditingController, Model, StylesProcessor } from '@ckeditor/ckeditor5-engine';
import Context from '../context.js';
import PluginCollection from '../plugincollection.js';
import CommandCollection from '../commandcollection.js';
import EditingKeystrokeHandler from '../editingkeystrokehandler.js';
/**
 * The class representing a basic, generic editor.
 *
 * Check out the list of its subclasses to learn about specific editor implementations.
 *
 * All editor implementations (like {@link module:editor-classic/classiceditor~ClassicEditor} or
 * {@link module:editor-inline/inlineeditor~InlineEditor}) should extend this class. They can add their
 * own methods and properties.
 *
 * When you are implementing a plugin, this editor represents the API
 * which your plugin can expect to get when using its {@link module:core/plugin~Plugin#editor} property.
 *
 * This API should be sufficient in order to implement the "editing" part of your feature
 * (schema definition, conversion, commands, keystrokes, etc.).
 * It does not define the editor UI, which is available only if
 * the specific editor implements also the {@link ~Editor#ui} property
 * (as most editor implementations do).
 */
var Editor = /*#__PURE__*/function (_ObservableMixin) {
  _inherits(Editor, _ObservableMixin);
  var _super = _createSuper(Editor);
  /**
   * Creates a new instance of the editor class.
   *
   * Usually, not to be used directly. See the static {@link module:core/editor/editor~Editor.create `create()`} method.
   *
   * @param config The editor configuration.
   */
  function Editor() {
    var _this;
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Editor);
    _this = _super.call(this);
    var constructor = _this.constructor;
    // Prefer the language passed as the argument to the constructor instead of the constructor's `defaultConfig`, if both are set.
    var language = config.language || constructor.defaultConfig && constructor.defaultConfig.language;
    _this._context = config.context || new Context({
      language: language
    });
    _this._context._addEditor(_assertThisInitialized(_this), !config.context);
    // Clone the plugins to make sure that the plugin array will not be shared
    // between editors and make the watchdog feature work correctly.
    var availablePlugins = Array.from(constructor.builtinPlugins || []);
    _this.config = new Config(config, constructor.defaultConfig);
    _this.config.define('plugins', availablePlugins);
    _this.config.define(_this._context._getEditorConfig());
    _this.plugins = new PluginCollection(_assertThisInitialized(_this), availablePlugins, _this._context.plugins);
    _this.locale = _this._context.locale;
    _this.t = _this.locale.t;
    _this._readOnlyLocks = new Set();
    _this.commands = new CommandCollection();
    _this.set('state', 'initializing');
    _this.once('ready', function () {
      return _this.state = 'ready';
    }, {
      priority: 'high'
    });
    _this.once('destroy', function () {
      return _this.state = 'destroyed';
    }, {
      priority: 'high'
    });
    _this.model = new Model();
    _this.on('change:isReadOnly', function () {
      _this.model.document.isReadOnly = _this.isReadOnly;
    });
    var stylesProcessor = new StylesProcessor();
    _this.data = new DataController(_this.model, stylesProcessor);
    _this.editing = new EditingController(_this.model, stylesProcessor);
    _this.editing.view.document.bind('isReadOnly').to(_assertThisInitialized(_this));
    _this.conversion = new Conversion([_this.editing.downcastDispatcher, _this.data.downcastDispatcher], _this.data.upcastDispatcher);
    _this.conversion.addAlias('dataDowncast', _this.data.downcastDispatcher);
    _this.conversion.addAlias('editingDowncast', _this.editing.downcastDispatcher);
    _this.keystrokes = new EditingKeystrokeHandler(_assertThisInitialized(_this));
    _this.keystrokes.listenTo(_this.editing.view.document);
    return _this;
  }
  /**
   * Defines whether the editor is in the read-only mode.
   *
   * In read-only mode the editor {@link #commands commands} are disabled so it is not possible
   * to modify the document by using them. Also, the editable element(s) become non-editable.
   *
   * In order to make the editor read-only, you need to call the {@link #enableReadOnlyMode} method:
   *
   * ```ts
   * editor.enableReadOnlyMode( 'feature-id' );
   * ```
   *
   * Later, to turn off the read-only mode, call {@link #disableReadOnlyMode}:
   *
   * ```ts
   * editor.disableReadOnlyMode( 'feature-id' );
   * ```
   *
   * @readonly
   * @observable
   */
  _createClass(Editor, [{
    key: "isReadOnly",
    get: function get() {
      return this._readOnlyLocks.size > 0;
    },
    set: function set(value) {
      /**
       * The {@link module:core/editor/editor~Editor#isReadOnly Editor#isReadOnly} property is read-only since version `34.0.0`
       * and can be set only using {@link module:core/editor/editor~Editor#enableReadOnlyMode `Editor#enableReadOnlyMode( lockId )`} and
       * {@link module:core/editor/editor~Editor#disableReadOnlyMode `Editor#disableReadOnlyMode( lockId )`}.
       *
       * Usage before version `34.0.0`:
       *
       * ```ts
       * editor.isReadOnly = true;
       * editor.isReadOnly = false;
       * ```
       *
       * Usage since version `34.0.0`:
       *
       * ```ts
       * editor.enableReadOnlyMode( 'my-feature-id' );
       * editor.disableReadOnlyMode( 'my-feature-id' );
       * ```
       *
       * @error editor-isreadonly-has-no-setter
       */
      throw new CKEditorError('editor-isreadonly-has-no-setter');
    }
    /**
     * Turns on the read-only mode in the editor.
     *
     * Editor can be switched to or out of the read-only mode by many features, under various circumstances. The editor supports locking
     * mechanism for the read-only mode. It enables easy control over the read-only mode when many features wants to turn it on or off at
     * the same time, without conflicting with each other. It guarantees that you will not make the editor editable accidentally (which
     * could lead to errors).
     *
     * Each read-only mode request is identified by a unique id (also called "lock"). If multiple plugins requested to turn on the
     * read-only mode, then, the editor will become editable only after all these plugins turn the read-only mode off (using the same ids).
     *
     * Note, that you cannot force the editor to disable the read-only mode if other plugins set it.
     *
     * After the first `enableReadOnlyMode()` call, the {@link #isReadOnly `isReadOnly` property} will be set to `true`:
     *
     * ```ts
     * editor.isReadOnly; // `false`.
     * editor.enableReadOnlyMode( 'my-feature-id' );
     * editor.isReadOnly; // `true`.
     * ```
     *
     * You can turn off the read-only mode ("clear the lock") using the {@link #disableReadOnlyMode `disableReadOnlyMode()`} method:
     *
     * ```ts
     * editor.enableReadOnlyMode( 'my-feature-id' );
     * // ...
     * editor.disableReadOnlyMode( 'my-feature-id' );
     * editor.isReadOnly; // `false`.
     * ```
     *
     * All "locks" need to be removed to enable editing:
     *
     * ```ts
     * editor.enableReadOnlyMode( 'my-feature-id' );
     * editor.enableReadOnlyMode( 'my-other-feature-id' );
     * // ...
     * editor.disableReadOnlyMode( 'my-feature-id' );
     * editor.isReadOnly; // `true`.
     * editor.disableReadOnlyMode( 'my-other-feature-id' );
     * editor.isReadOnly; // `false`.
     * ```
     *
     * @param lockId A unique ID for setting the editor to the read-only state.
     */
  }, {
    key: "enableReadOnlyMode",
    value: function enableReadOnlyMode(lockId) {
      if (typeof lockId !== 'string' && typeof lockId !== 'symbol') {
        /**
         * The lock ID is missing or it is not a string or symbol.
         *
         * @error editor-read-only-lock-id-invalid
         */
        throw new CKEditorError('editor-read-only-lock-id-invalid', null, {
          lockId: lockId
        });
      }
      if (this._readOnlyLocks.has(lockId)) {
        return;
      }
      this._readOnlyLocks.add(lockId);
      if (this._readOnlyLocks.size === 1) {
        // Manually fire the `change:isReadOnly` event as only getter is provided.
        this.fire('change:isReadOnly', 'isReadOnly', true, false);
      }
    }
    /**
     * Removes the read-only lock from the editor with given lock ID.
     *
     * When no lock is present on the editor anymore, then the {@link #isReadOnly `isReadOnly` property} will be set to `false`.
     *
     * @param lockId The lock ID for setting the editor to the read-only state.
     */
  }, {
    key: "disableReadOnlyMode",
    value: function disableReadOnlyMode(lockId) {
      if (typeof lockId !== 'string' && typeof lockId !== 'symbol') {
        throw new CKEditorError('editor-read-only-lock-id-invalid', null, {
          lockId: lockId
        });
      }
      if (!this._readOnlyLocks.has(lockId)) {
        return;
      }
      this._readOnlyLocks.delete(lockId);
      if (this._readOnlyLocks.size === 0) {
        // Manually fire the `change:isReadOnly` event as only getter is provided.
        this.fire('change:isReadOnly', 'isReadOnly', false, true);
      }
    }
    /**
     * Loads and initializes plugins specified in the configuration.
     *
     * @returns A promise which resolves once the initialization is completed, providing an array of loaded plugins.
     */
  }, {
    key: "initPlugins",
    value: function initPlugins() {
      var config = this.config;
      var plugins = config.get('plugins');
      var removePlugins = config.get('removePlugins') || [];
      var extraPlugins = config.get('extraPlugins') || [];
      var substitutePlugins = config.get('substitutePlugins') || [];
      return this.plugins.init(plugins.concat(extraPlugins), removePlugins, substitutePlugins);
    }
    /**
     * Destroys the editor instance, releasing all resources used by it.
     *
     * **Note** The editor cannot be destroyed during the initialization phase so if it is called
     * while the editor {@link #state is being initialized}, it will wait for the editor initialization before destroying it.
     *
     * @fires destroy
     * @returns A promise that resolves once the editor instance is fully destroyed.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      var _this2 = this;
      var readyPromise = Promise.resolve();
      if (this.state == 'initializing') {
        readyPromise = new Promise(function (resolve) {
          return _this2.once('ready', resolve);
        });
      }
      return readyPromise.then(function () {
        _this2.fire('destroy');
        _this2.stopListening();
        _this2.commands.destroy();
      }).then(function () {
        return _this2.plugins.destroy();
      }).then(function () {
        _this2.model.destroy();
        _this2.data.destroy();
        _this2.editing.destroy();
        _this2.keystrokes.destroy();
      })
      // Remove the editor from the context.
      // When the context was created by this editor, the context will be destroyed.
      .then(function () {
        return _this2._context._removeEditor(_this2);
      });
    }
    /**
     * Executes the specified command with given parameters.
     *
     * Shorthand for:
     *
     * ```ts
     * editor.commands.get( commandName ).execute( ... );
     * ```
     *
     * @param commandName The name of the command to execute.
     * @param commandParams Command parameters.
     * @returns The value returned by the {@link module:core/commandcollection~CommandCollection#execute `commands.execute()`}.
     */
  }, {
    key: "execute",
    value: function execute(commandName) {
      try {
        var _this$commands;
        for (var _len = arguments.length, commandParams = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          commandParams[_key - 1] = arguments[_key];
        }
        return (_this$commands = this.commands).execute.apply(_this$commands, [commandName].concat(commandParams));
      } catch (err) {
        // @if CK_DEBUG // throw err;
        /* istanbul ignore next -- @preserve */
        CKEditorError.rethrowUnexpectedError(err, this);
      }
    }
    /**
     * Focuses the editor.
     *
     * **Note** To explicitly focus the editing area of the editor, use the
     * {@link module:engine/view/view~View#focus `editor.editing.view.focus()`} method of the editing view.
     *
     * Check out the {@glink framework/deep-dive/ui/focus-tracking#focus-in-the-editor-ui Focus in the editor UI} section
     * of the {@glink framework/deep-dive/ui/focus-tracking Deep dive into focus tracking} guide to learn more.
     */
  }, {
    key: "focus",
    value: function focus() {
      this.editing.view.focus();
    }
    /* istanbul ignore next -- @preserve */
    /**
     * Creates and initializes a new editor instance.
     *
     * This is an abstract method. Every editor type needs to implement its own initialization logic.
     *
     * See the `create()` methods of the existing editor types to learn how to use them:
     *
     * * {@link module:editor-classic/classiceditor~ClassicEditor.create `ClassicEditor.create()`}
     * * {@link module:editor-balloon/ballooneditor~BalloonEditor.create `BalloonEditor.create()`}
     * * {@link module:editor-decoupled/decouplededitor~DecoupledEditor.create `DecoupledEditor.create()`}
     * * {@link module:editor-inline/inlineeditor~InlineEditor.create `InlineEditor.create()`}
     */
  }], [{
    key: "create",
    value: function create() {
      throw new Error('This is an abstract method.');
    }
  }]);
  return Editor;
}(ObservableMixin());
/**
 * This error is thrown when trying to pass a `<textarea>` element to a `create()` function of an editor class.
 *
 * The only editor type which can be initialized on `<textarea>` elements is
 * the {@glink installation/getting-started/predefined-builds#classic-editor classic editor}.
 * This editor hides the passed element and inserts its own UI next to it. Other types of editors reuse the passed element as their root
 * editable element and therefore `<textarea>` is not appropriate for them. Use a `<div>` or another text container instead:
 *
 * ```html
 * <div id="editor">
 * 	<p>Initial content.</p>
 * </div>
 * ```
 *
 * @error editor-wrong-element
 */
export { Editor as default };